import React, { useState, useEffect } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import styles from "./files.module.css";
import ZoomableImage from "../../../components/ui/zoomable-image";
import { MyIcons } from "../../../components/icons/my-icons";
import Input from "../../../components/ui/input";
import { API_BASE_URL } from "../../../config";
import usePageTitle from "../../../hooks/usePageTitle";

const FilesComponent = ({ expId }) => {
  usePageTitle(`Expediente ${expId}`, 'Archivos');
  const { isLockedByOther } = useOutletContext();
  // Estado para controlar la carpeta actual y datos reales
  const [currentFolder, setCurrentFolder] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [archivosAbiertos, setArchivosAbiertos] = useState([]);
  const [archivoActivo, setArchivoActivo] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Función para alternar fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  


  // Cargar datos reales del backend
  useEffect(() => {
    async function fetchData() {
      console.log('=== DEBUG FETCH FILES ===');
      console.log('expId:', expId);
      console.log('currentFolder:', currentFolder);
      
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('authToken');
        const cleanExpId = expId.toString().replace(/\s+/g, '');
        const url = `${API_BASE_URL}/archivos/${cleanExpId}${currentFolder ? `?path=${encodeURIComponent(currentFolder)}` : ''}`;
        
        console.log('URL completa:', url);
        console.log('Token presente:', !!token);
        
        const res = await fetch(url, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        console.log('Response status:', res.status);
        console.log('Response ok:', res.ok);
        
        if (!res.ok) {
          const errorText = await res.text();
          console.log('Error response:', errorText);
          throw new Error(`Error al obtener archivos: ${res.status} - ${errorText}`);
        }
        
        const data = await res.json();
        console.log('Datos recibidos del backend:', data);
        console.log('Tipo de datos:', typeof data);
        console.log('Es array:', Array.isArray(data));
        console.log('Keys del objeto:', Object.keys(data));
        
        // Debug específico para URLs de archivos
        if (Array.isArray(data) && data.length > 0) {
          console.log('Ejemplo de archivo del backend:', data[0]);
          console.log('Propiedades del primer archivo:', Object.keys(data[0]));
        }
        
        setItems(data);
      } catch (err) {
        console.error('Error en fetch:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [expId, currentFolder]);

  const getFileIcon = (fileName, isSelected = false) => {
    if (!fileName || typeof fileName !== 'string') {
      const color = isSelected ? "white" : "var(--dark-grey)";
      return <MyIcons name="fileFilled" width={24} height={24} color={color} />;
    }
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const color = isSelected ? "white" : "var(--dark-grey)";
    const mainColor = isSelected ? "white" : "var(--main)";
    
    if (ext === "pdf") return <MyIcons name="pdf" width={24} height={24} color={color} />;
    if (["jpg","jpeg","png","gif","webp","bmp","svg"].includes(ext)) return <MyIcons name="image" width={24} height={24} color={color} />;
    if (["doc","docx"].includes(ext)) return <MyIcons name="fileFilled" width={24} height={24} color={mainColor} />;
    if (["txt","log","md","json","xml","csv"].includes(ext)) return <MyIcons name="fileFilled" width={24} height={24} color={color} />;
    return <MyIcons name="fileFilled" width={24} height={24} color={color} />;
  };

  const handleFolderClick = (folderName) => {
    setCurrentFolder(currentFolder ? `${currentFolder}/${folderName}` : folderName);
  };

  const handleBackClick = () => {
    if (!currentFolder) return;
    const parts = currentFolder.split('/');
    parts.pop();
    setCurrentFolder(parts.join('/'));
  };

  const handleFileClick = (file) => {
    console.log('Archivo seleccionado:', file);
    console.log('Archivos abiertos actuales:', archivosAbiertos);
    
    const timestamp = new Date().getTime();
    const relativePath = currentFolder ? `${currentFolder}/${file.name}` : file.name;
    // Calcular el año del expediente a partir de su ID
    const cleanExpId = expId.toString().replace(/\s+/g, '');
    const expedienteYear = Math.floor(parseInt(cleanExpId) / 10000) + 2000;
    
    // Construir el path completo para el servidor (sin codificar aún)
    const serverPath = `${expedienteYear}/${cleanExpId}/${relativePath}`;
    
    // Usar el nuevo endpoint /api/files/serve con query parameter
    // Esto evita problemas con caracteres especiales en la URL path
    const workingUrl = `${API_BASE_URL}/api/files/serve?path=${encodeURIComponent(serverPath)}&t=${timestamp}`;
    
    const possibleUrls = [
      workingUrl,
      file.url ? `${file.url}${file.url.includes('?') ? '&' : '?'}t=${timestamp}` : null,
    ].filter(Boolean);
    
    const processedFile = {
      ...file,
      expId: expId,
      currentFolder: currentFolder,
      fullPath: relativePath, // Sin codificar para comparación
      url: workingUrl, // URL con query parameter
      possibleUrls: possibleUrls
    };
    
    console.log('Archivo procesado:', processedFile);
    console.log('URL con query parameter:', workingUrl);
    
    // Verificar si el archivo ya está abierto
    const yaAbierto = archivosAbiertos.find(a => a.fullPath === processedFile.fullPath);
    
    console.log('Ya abierto?:', yaAbierto);
    
    if (yaAbierto) {
      // Si ya está abierto, solo activarlo
      console.log('Activando archivo existente');
      setArchivoActivo(yaAbierto);
    } else {
      // Si no está abierto, agregarlo y activarlo
      console.log('Agregando nuevo archivo');
      const nuevosArchivos = [...archivosAbiertos, processedFile];
      console.log('Nuevos archivos:', nuevosArchivos);
      setArchivosAbiertos(nuevosArchivos);
      setArchivoActivo(processedFile);
    }
    
    // Detectar si el archivo necesita preview en fullscreen
    const ext = file.name.split('.').pop().toLowerCase();
    const needsFullscreen = ["pdf","doc","docx","xls","xlsx","ppt","pptx","mp4","webm","avi","mov","wmv","flv","m4v","3gp"].includes(ext);
    setIsFullscreen(needsFullscreen);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const renderContent = () => {
    console.log('=== DEBUG RENDER CONTENT ===');
    console.log('Loading:', loading);
    console.log('Error:', error);
    console.log('Items:', items);
    console.log('Items length:', items?.length);
    console.log('Search value:', searchValue);
    
    if (loading) return <div className={styles.noFiles}>Cargando...</div>;
    if (error) return <div className={styles.noFiles}>Error: {error}</div>;
    
    // Manejar diferentes estructuras de respuesta de la API
    let folders = [];
    let files = [];
    
    console.log('Analizando estructura de datos...');
    
    if (Array.isArray(items)) {
      console.log('Items es un array directo con', items.length, 'elementos');
      console.log('Primer elemento:', items[0]);
      
      folders = items.filter(item => {
        const isDir = item.type === 'directory' || item.isDirectory;
        console.log('Item:', item.name, 'es directorio:', isDir);
        return isDir;
      });
      
      files = items.filter(item => {
        const isFile = item.type === 'file' || item.isFile || (!item.type && !item.isDirectory);
        console.log('Item:', item.name, 'es archivo:', isFile);
        return isFile;
      });
    } else if (items && items.files && Array.isArray(items.files)) {
      console.log('Items tiene propiedad files');
      folders = items.folders || [];
      files = items.files || [];
    } else if (items && items.data && Array.isArray(items.data)) {
      console.log('Items tiene propiedad data');
      folders = items.data.filter(item => item.type === 'directory' || item.isDirectory);
      files = items.data.filter(item => item.type === 'file' || item.isFile || (!item.type && !item.isDirectory));
    } else {
      console.log('Estructura de datos no reconocida:', typeof items, items);
      // Intentar tratar todo como archivos si no hay estructura clara
      if (items && typeof items === 'object') {
        const allKeys = Object.keys(items);
        console.log('Keys disponibles:', allKeys);
        
        // Buscar arrays en las propiedades
        for (const key of allKeys) {
          if (Array.isArray(items[key])) {
            console.log(`Encontrado array en key "${key}":`, items[key]);
            files = items[key];
            break;
          }
        }
        
        // Si no encontramos arrays, pero items tiene propiedades, tratarlo como un array de archivos
        if (files.length === 0 && allKeys.length > 0) {
          console.log('Intentando convertir objeto a array de archivos...');
          files = Object.values(items).filter(item => item && typeof item === 'object' && item.name);
        }
      }
    }
    
    // FALLBACK: Si items es un array pero los filtros no funcionaron, usar todos como archivos
    if (Array.isArray(items) && files.length === 0 && folders.length === 0 && items.length > 0) {
      console.log('FALLBACK: Tratando todos los items como archivos');
      files = items;
    }
    
    console.log('RESULTADO FINAL:');
    console.log('Folders encontradas:', folders.length, folders);
    console.log('Files encontrados:', files.length, files);
    
    const filteredFiles = searchValue
      ? files.filter((file) => file.name.toLowerCase().includes(searchValue.toLowerCase()))
      : files;
      
    console.log('Filtered files:', filteredFiles.length, filteredFiles);

    return (
      <>
        {currentFolder && (
          <div className={styles.folderHeader}>
            <button className={styles.backButton} onClick={handleBackClick}>
              <MyIcons
                name="arrowLeft"
                width={24}
                height={24}
                color={"var(--black)"}
              />
            </button>
            <h2 className={styles.folderTitle}>
              <MyIcons
                name="folder"
                width={24}
                height={24}
                color={"var(--main)"}
              />
              {currentFolder}
            </h2>
          </div>
        )}

        {folders.length > 0 && (
          <div className={styles.foldersGrid}>
            {folders.map((folder) => (
              <div
                key={folder.name}
                className={styles.folderItem}
                onClick={() => handleFolderClick(folder.name)}
              >
                <div>
                  <MyIcons
                    name="folder"
                    width={48}
                    height={48}
                    color={"var(--main)"}
                  />
                </div>
                <div className={styles.folderName}>{folder.name.replace(/_/g, '_\u200B')}</div>
                <div className={styles.fileCount}>
                  {folder.count} archivos
                </div>
              </div>
            ))}
          </div>
        )}
        {items.length=== 0 ? (
          <div className={styles.noFiles}>
            No se encontraron archivos
          </div>
        ) : (
          <div className={styles.filesGrid}>
            {filteredFiles.map((file) => {
              const relativePath = currentFolder ? `${currentFolder}/${file.name}` : file.name;
              const estaAbierto = archivosAbiertos.some(a => a.fullPath === relativePath);
              const estaActivo = archivoActivo && archivoActivo.fullPath === relativePath;
              
              return (
                <div
                  key={file.name}
                  className={`${styles.fileItem} ${estaActivo ? styles.fileSelected : ''}`}
                  onClick={() => handleFileClick(file)}
                >
                  <div className={styles.fileIconContainer}>
                    {getFileIcon(file.name, estaActivo)}
                  </div>
                  <div>
                    <div className={styles.fileName}>{file.name}</div>
                  </div>
                  {estaAbierto && (
                    <div className={styles.openIndicator}>•</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.filesContainer}>
      <div className={styles.mainLayout}>
        <div className={styles.content}>{renderContent()}</div>
        
        {archivosAbiertos.length > 0 && !isFullscreen && (
          <div className={styles.previewContainer}>
            {/* Pestañas */}
            <div className={styles.tabsContainer}>
              {archivosAbiertos.map((archivo) => (
                <div
                  key={archivo.fullPath}
                  className={`${styles.tab} ${archivoActivo?.fullPath === archivo.fullPath ? styles.tabActive : ''}`}
                  onClick={() => setArchivoActivo(archivo)}
                >
                  <span className={styles.tabName}>{archivo.name}</span>
                  <button
                    className={styles.tabClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      const nuevosArchivos = archivosAbiertos.filter(a => a.fullPath !== archivo.fullPath);
                      setArchivosAbiertos(nuevosArchivos);
                      if (archivoActivo?.fullPath === archivo.fullPath) {
                        setArchivoActivo(nuevosArchivos[nuevosArchivos.length - 1] || null);
                      }
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            
            {/* Contenido del archivo activo */}
            {archivoActivo && (
              <>
                <div className={styles.previewHeader}>
                  <h3 className={styles.previewTitle}>{archivoActivo.name}</h3>
                  <div style={{display: 'flex', gap: '8px'}}>
                    <button 
                      className={styles.expandButton} 
                      onClick={() => setIsFullscreen(true)}
                      title="Ampliar vista"
                    >
                      ⛶
                    </button>
                  </div>
                </div>
                <div className={styles.previewContent}>
                  <ArchivoViewer 
                    archivo={archivoActivo} 
                    onToggleFullscreen={toggleFullscreen}
                    isFullscreen={false}
                  />
                </div>
              </>
            )}
          </div>
        )}
        
        {archivoActivo && isFullscreen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 24px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white'
            }}>
              <h3 style={{margin: 0, fontSize: '18px'}}>{archivoActivo.name}</h3>
              <div style={{display: 'flex', gap: '12px'}}>
                <button 
                  onClick={() => setIsFullscreen(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  🗗 Reducir
                </button>
                <button 
                  onClick={() => {
                    setIsFullscreen(false);
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ✕ Cerrar
                </button>
              </div>
            </div>
            <div style={{
              flex: 1,
              padding: '16px',
              overflow: 'auto'
            }}>
              <ArchivoViewer 
                archivo={archivoActivo} 
                onToggleFullscreen={toggleFullscreen}
                isFullscreen={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente visor de archivos
function ArchivoViewer({ archivo, onToggleFullscreen, isFullscreen }) {
  if (!archivo) return null;
  
  console.log('ArchivoViewer recibió:', archivo); // Debug
  
  const ext = archivo.name.split('.').pop().toLowerCase();
  const isImage = ["jpg","jpeg","png","gif","webp","bmp","svg","heic","heif"].includes(ext);
  
  // Determinar si este archivo soporta fullscreen
  const canFullscreen = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 
                         'mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv',
                         'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
  
  console.log('Extensión detectada:', ext, 'Es imagen:', isImage); // Debug
  
  // Si es PDF, usar el mismo sistema de blob que las imágenes
  if (ext === 'pdf') {
    console.log('Cargando PDF con autenticación:', archivo.url);
    
    const AuthenticatedPDF = () => {
      const [pdfSrc, setPdfSrc] = React.useState(null);
      const [loading, setLoading] = React.useState(true);
      const [error, setError] = React.useState(null);
      const [urlIndex, setUrlIndex] = React.useState(0);
      
      const urls = React.useMemo(() => archivo.possibleUrls || [archivo.url], []);
      
      React.useEffect(() => {
        const loadPDF = async () => {
          if (urlIndex >= urls.length) {
            setError('No se pudo cargar el PDF desde ninguna URL');
            setLoading(false);
            return;
          }
          
          const currentUrl = urls[urlIndex];
          console.log('Intentando cargar PDF desde:', currentUrl);
          
          try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(currentUrl, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            console.log('Response status para PDF:', response.status);
            
            if (response.ok) {
              const blob = await response.blob();
              const pdfUrl = URL.createObjectURL(blob);
              setPdfSrc(pdfUrl);
              setError(null);
              setLoading(false);
              console.log('PDF cargado correctamente como blob:', archivo.name);
            } else {
              console.error('Error en respuesta PDF:', response.status, response.statusText);
              setUrlIndex(urlIndex + 1);
            }
          } catch (err) {
            console.error('Error fetching PDF:', err);
            setUrlIndex(urlIndex + 1);
          }
        };
        
        loadPDF();
      }, [urlIndex, urls]);
      
      // Cleanup: liberar URL del blob cuando el componente se desmonte
      React.useEffect(() => {
        return () => {
          if (pdfSrc && pdfSrc.startsWith('blob:')) {
            URL.revokeObjectURL(pdfSrc);
          }
        };
      }, [pdfSrc]);
      
      if (loading) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#666'
          }}>
            <span>🔄 Cargando PDF...</span>
          </div>
        );
      }
      
      if (error || !pdfSrc) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column',
            color: '#666',
            gap: '10px'
          }}>
            <span>❌ No se pudo cargar el PDF</span>
            <span style={{fontSize: '12px'}}>{archivo.name}</span>
            <a 
              href={archivo.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{color: '#007bff', fontSize: '12px'}}
            >
              Abrir en nueva pestaña ↗
            </a>
          </div>
        );
      }
      
      // Añadir parámetros para mejorar visualización del PDF sin zoom
      const pdfSrcWithParams = pdfSrc ? `${pdfSrc}#toolbar=1&navpanes=0&scrollbar=1` : pdfSrc;
      
      return (
        <iframe 
          src={pdfSrcWithParams} 
          width="100%" 
          height="100%" 
          title={archivo.name}
          style={{border: 'none', borderRadius: '4px', minHeight: '600px'}}
        />
      );
    };
    
    return (
      <div style={{width: '100%', height: '100%', position: 'relative'}}>
        {canFullscreen && onToggleFullscreen && (
          <button
            onClick={onToggleFullscreen}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 1000,
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
            title={isFullscreen ? 'Salir de pantalla completa' : 'Ver en pantalla completa'}
          >
            {isFullscreen ? '📱' : '🔍'}
          </button>
        )}
        <AuthenticatedPDF />
      </div>
    );
  }
  if (isImage) {
    console.log('Cargando imagen con autenticación:', archivo.url); // Debug
    
    const AuthenticatedImage = () => {
      const [imageSrc, setImageSrc] = React.useState(null);
      const [loading, setLoading] = React.useState(true);
      const [error, setError] = React.useState(null);
      const [urlIndex, setUrlIndex] = React.useState(0);
      
      const urls = React.useMemo(() => archivo.possibleUrls || [archivo.url], []);
      
      React.useEffect(() => {
        const loadImage = async () => {
          if (urlIndex >= urls.length) {
            setError('No se pudo cargar la imagen desde ninguna URL');
            setLoading(false);
            return;
          }
          
          const currentUrl = urls[urlIndex];
          console.log('Intentando cargar imagen desde:', currentUrl);
          
          try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(currentUrl, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            console.log('Response status para imagen:', response.status);
            
            if (response.ok) {
              let blob = await response.blob();
              // HEIC/HEIF conversion
              const lowerName = (archivo.name || '').toLowerCase();
              if (blob.type === 'image/heic' || blob.type === 'image/heif' || lowerName.endsWith('.heic') || lowerName.endsWith('.heif')) {
                try {
                  const heic2any = (await import('heic2any')).default;
                  const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.85 });
                  blob = Array.isArray(converted) ? converted[0] : converted;
                } catch (heicErr) {
                  console.warn('HEIC conversion failed, using original blob:', heicErr);
                }
              }
              const imageUrl = URL.createObjectURL(blob);
              setImageSrc(imageUrl);
              setError(null);
              setLoading(false);
              console.log('Imagen cargada correctamente como blob:', archivo.name);
            } else {
              console.error('Error en respuesta:', response.status, response.statusText);
              // Intentar siguiente URL
              setUrlIndex(urlIndex + 1);
            }
          } catch (err) {
            console.error('Error fetching imagen:', err);
            // Intentar siguiente URL
            setUrlIndex(urlIndex + 1);
          }
        };
        
        loadImage();
      }, [urlIndex, urls]);
      
      // Cleanup: liberar URL del blob cuando el componente se desmonte
      React.useEffect(() => {
        return () => {
          if (imageSrc && imageSrc.startsWith('blob:')) {
            URL.revokeObjectURL(imageSrc);
          }
        };
      }, [imageSrc]);
      
      if (loading) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#666'
          }}>
            <span>🔄 Cargando imagen...</span>
          </div>
        );
      }
      
      if (error || !imageSrc) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column',
            color: '#666',
            gap: '10px'
          }}>
            <span>❌ No se pudo cargar la imagen</span>
            <span style={{fontSize: '12px'}}>{archivo.name}</span>
            <a 
              href={archivo.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{color: '#007bff', fontSize: '12px'}}
            >
              Abrir en nueva pestaña ↗
            </a>
          </div>
        );
      }
      
      return (
        <ZoomableImage src={imageSrc} alt={archivo.name} />
      );
    };
    
    return (
      <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative'}}>
        <AuthenticatedImage />
      </div>
    );
  }
  
  // Soporte para archivos de texto
  if (["txt","log","md","json","xml","csv","js","css","html","py","java","cpp","c","h"].includes(ext)) {
    console.log('Cargando archivo de texto:', archivo.url);
    
    const TextViewer = () => {
      const [textContent, setTextContent] = React.useState('');
      const [loading, setLoading] = React.useState(true);
      const [error, setError] = React.useState(null);
      const [urlIndex, setUrlIndex] = React.useState(0);
      
      const urls = React.useMemo(() => archivo.possibleUrls || [archivo.url], []);
      
      React.useEffect(() => {
        const loadText = async () => {
          if (urlIndex >= urls.length) {
            setError('No se pudo cargar el archivo desde ninguna URL');
            setLoading(false);
            return;
          }
          
          const currentUrl = urls[urlIndex];
          console.log('Intentando cargar texto desde:', currentUrl);
          
          try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(currentUrl, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            console.log('Response status para texto:', response.status);
            
            if (response.ok) {
              const text = await response.text();
              setTextContent(text);
              setError(null);
              setLoading(false);
              console.log('Archivo de texto cargado correctamente:', archivo.name);
            } else {
              console.error('Error en respuesta:', response.status, response.statusText);
              setUrlIndex(urlIndex + 1);
            }
          } catch (err) {
            console.error('Error fetching texto:', err);
            setUrlIndex(urlIndex + 1);
          }
        };
        
        loadText();
      }, [urlIndex, urls]);
      
      if (loading) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#666'
          }}>
            <span>🔄 Cargando archivo...</span>
          </div>
        );
      }
      
      if (error || !textContent) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column',
            color: '#666',
            gap: '10px'
          }}>
            <span>❌ No se pudo cargar el archivo</span>
            <span style={{fontSize: '12px'}}>{archivo.name}</span>
            <a 
              href={archivo.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{color: '#007bff', fontSize: '12px'}}
            >
              Abrir en nueva pestaña ↗
            </a>
          </div>
        );
      }
      
      return (
        <div style={{
          width: '100%', 
          height: '100%', 
          overflow: 'auto',
          padding: '16px',
          fontFamily: 'monospace',
          fontSize: '13px',
          lineHeight: '1.4',
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '4px'
        }}>
          <pre style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: '#212529'
          }}>
            {textContent}
          </pre>
        </div>
      );
    };
    
    return <TextViewer />;
  }
  
  // Para Word, Excel, etc. - usar Microsoft Office Online Viewer
  if (["doc","docx","xls","xlsx","ppt","pptx"].includes(ext)) {
    console.log('Cargando documento Office con autenticación:', archivo.url);
    
    // Intentar usar Microsoft Office Online Viewer
    const OfficeViewer = () => {
      const [viewerState, setViewerState] = React.useState('loading'); // loading, iframe, fallback
      const [errorMsg, setErrorMsg] = React.useState('');
      
      // Microsoft Office Online Viewer URL
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(archivo.url)}`;
      
      React.useEffect(() => {
        // Intentar cargar con Microsoft Office Online primero
        const testFrame = document.createElement('iframe');
        testFrame.style.display = 'none';
        testFrame.src = officeViewerUrl;
        
        const timeout = setTimeout(() => {
          setViewerState('fallback');
          setErrorMsg('El visor de Office Online no está disponible');
          document.body.removeChild(testFrame);
        }, 5000);
        
        testFrame.onload = () => {
          clearTimeout(timeout);
          setViewerState('iframe');
          document.body.removeChild(testFrame);
        };
        
        testFrame.onerror = () => {
          clearTimeout(timeout);
          setViewerState('fallback');
          setErrorMsg('Error al cargar el visor de Office Online');
          document.body.removeChild(testFrame);
        };
        
        document.body.appendChild(testFrame);
        
        return () => {
          clearTimeout(timeout);
          if (document.body.contains(testFrame)) {
            document.body.removeChild(testFrame);
          }
        };
      }, [officeViewerUrl]);
      
      if (viewerState === 'loading') {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#666',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <span>🔄 Cargando visor de documentos...</span>
            <span style={{fontSize: '12px'}}>Intentando Microsoft Office Online</span>
          </div>
        );
      }
      
      if (viewerState === 'iframe') {
        return (
          <div style={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
            <iframe 
              src={officeViewerUrl}
              width="100%" 
              height="100%" 
              title={archivo.name}
              style={{border: 'none', borderRadius: '4px', flex: 1}}
            />
            <div style={{marginTop:'8px', textAlign: 'center', fontSize: '12px'}}>
              <a href={archivo.url} target="_blank" rel="noopener noreferrer" style={{color: '#007bff'}}>
                Abrir archivo original ↗
              </a>
            </div>
          </div>
        );
      }
      
      // Fallback cuando Office Online no funciona
      return (
        <div style={{
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          color: '#666',
          gap: '20px'
        }}>
          <div style={{fontSize: '48px'}}>📄</div>
          <span style={{fontSize: '16px', fontWeight: 'bold', color: '#333'}}>{archivo.name}</span>
          <span style={{fontSize: '14px', color: '#666', textAlign: 'center', maxWidth: '300px'}}>
            {errorMsg || 'Este documento necesita ser abierto con una aplicación externa'}
          </span>
          <div style={{display: 'flex', gap: '12px', flexDirection: 'column', alignItems: 'center'}}>
            <a 
              href={archivo.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: '#007bff',
                textDecoration: 'none',
                padding: '12px 24px',
                background: '#f0f8ff',
                borderRadius: '6px',
                border: '1px solid #007bff',
                fontWeight: 'bold'
              }}
            >
              � Abrir documento
            </a>
            {archivo.openUrl && (
              <a 
                href={archivo.openUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#28a745',
                  textDecoration: 'none',
                  padding: '8px 16px',
                  background: '#f0fff0',
                  borderRadius: '4px',
                  border: '1px solid #28a745'
                }}
              >
                🖥️ Abrir con aplicación local ↗
              </a>
            )}
          </div>
        </div>
      );
    };
    
    return (
      <div style={{width: '100%', height: '100%', position: 'relative'}}>
        {canFullscreen && onToggleFullscreen && (
          <button
            onClick={onToggleFullscreen}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 1000,
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
            title={isFullscreen ? 'Salir de pantalla completa' : 'Ver en pantalla completa'}
          >
            {isFullscreen ? '📱' : '🔍'}
          </button>
        )}
        <OfficeViewer />
      </div>
    );
  }
  
  // Soporte para archivos multimedia (video y audio)
  if (["mp4","webm","avi","mov","wmv","flv","m4v","3gp"].includes(ext)) {
    console.log('Cargando video con autenticación:', archivo.url);
    
    const AuthenticatedVideo = () => {
      const [videoSrc, setVideoSrc] = React.useState(null);
      const [loading, setLoading] = React.useState(true);
      const [error, setError] = React.useState(null);
      const [urlIndex, setUrlIndex] = React.useState(0);
      
      const urls = React.useMemo(() => archivo.possibleUrls || [archivo.url], []);
      
      React.useEffect(() => {
        const loadVideo = async () => {
          if (urlIndex >= urls.length) {
            setError('No se pudo cargar el video desde ninguna URL');
            setLoading(false);
            return;
          }
          
          const currentUrl = urls[urlIndex];
          console.log('Intentando cargar video desde:', currentUrl);
          
          try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(currentUrl, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            console.log('Response status para video:', response.status);
            
            if (response.ok) {
              const blob = await response.blob();
              const videoUrl = URL.createObjectURL(blob);
              setVideoSrc(videoUrl);
              setError(null);
              setLoading(false);
              console.log('Video cargado correctamente como blob:', archivo.name);
            } else {
              console.error('Error en respuesta video:', response.status, response.statusText);
              setUrlIndex(urlIndex + 1);
            }
          } catch (err) {
            console.error('Error fetching video:', err);
            setUrlIndex(urlIndex + 1);
          }
        };
        
        loadVideo();
      }, [urlIndex, urls]);
      
      // Cleanup: liberar URL del blob cuando el componente se desmonte
      React.useEffect(() => {
        return () => {
          if (videoSrc && videoSrc.startsWith('blob:')) {
            URL.revokeObjectURL(videoSrc);
          }
        };
      }, [videoSrc]);
      
      if (loading) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#666'
          }}>
            <span>🔄 Cargando video...</span>
          </div>
        );
      }
      
      if (error || !videoSrc) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column',
            color: '#666',
            gap: '10px'
          }}>
            <span>❌ No se pudo cargar el video</span>
            <span style={{fontSize: '12px'}}>{archivo.name}</span>
            <a 
              href={archivo.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{color: '#007bff', fontSize: '12px'}}
            >
              Abrir en nueva pestaña ↗
            </a>
          </div>
        );
      }
      
      return (
        <video 
          src={videoSrc}
          controls 
          style={{
            maxWidth:'100%',
            maxHeight:'100%',
            objectFit: 'contain'
          }}
        >
          Tu navegador no soporta el elemento video.
        </video>
      );
    };
    
    return (
      <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'}}>
        {canFullscreen && onToggleFullscreen && (
          <button
            onClick={onToggleFullscreen}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 1000,
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
            title={isFullscreen ? 'Salir de pantalla completa' : 'Ver en pantalla completa'}
          >
            {isFullscreen ? '📱' : '🔍'}
          </button>
        )}
        <AuthenticatedVideo />
      </div>
    );
  }
  
  // Soporte para archivos de audio
  if (["mp3","wav","ogg","aac","m4a","wma","flac"].includes(ext)) {
    console.log('Cargando audio con autenticación:', archivo.url);
    
    const AuthenticatedAudio = () => {
      const [audioSrc, setAudioSrc] = React.useState(null);
      const [loading, setLoading] = React.useState(true);
      const [error, setError] = React.useState(null);
      const [urlIndex, setUrlIndex] = React.useState(0);
      
      const urls = React.useMemo(() => archivo.possibleUrls || [archivo.url], []);
      
      React.useEffect(() => {
        const loadAudio = async () => {
          if (urlIndex >= urls.length) {
            setError('No se pudo cargar el audio desde ninguna URL');
            setLoading(false);
            return;
          }
          
          const currentUrl = urls[urlIndex];
          console.log('Intentando cargar audio desde:', currentUrl);
          
          try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(currentUrl, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            console.log('Response status para audio:', response.status);
            
            if (response.ok) {
              const blob = await response.blob();
              const audioUrl = URL.createObjectURL(blob);
              setAudioSrc(audioUrl);
              setError(null);
              setLoading(false);
              console.log('Audio cargado correctamente como blob:', archivo.name);
            } else {
              console.error('Error en respuesta audio:', response.status, response.statusText);
              setUrlIndex(urlIndex + 1);
            }
          } catch (err) {
            console.error('Error fetching audio:', err);
            setUrlIndex(urlIndex + 1);
          }
        };
        
        loadAudio();
      }, [urlIndex, urls]);
      
      // Cleanup: liberar URL del blob cuando el componente se desmonte
      React.useEffect(() => {
        return () => {
          if (audioSrc && audioSrc.startsWith('blob:')) {
            URL.revokeObjectURL(audioSrc);
          }
        };
      }, [audioSrc]);
      
      if (loading) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#666'
          }}>
            <span>🔄 Cargando audio...</span>
          </div>
        );
      }
      
      if (error || !audioSrc) {
        return (
          <div style={{
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column',
            color: '#666',
            gap: '10px'
          }}>
            <span>❌ No se pudo cargar el audio</span>
            <span style={{fontSize: '12px'}}>{archivo.name}</span>
            <a 
              href={archivo.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{color: '#007bff', fontSize: '12px'}}
            >
              Abrir en nueva pestaña ↗
            </a>
          </div>
        );
      }
      
      return (
        <div style={{
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{fontSize: '48px'}}>🎵</div>
          <div style={{fontSize: '16px', fontWeight: 'bold', color: '#333'}}>{archivo.name}</div>
          <audio 
            src={audioSrc}
            controls 
            style={{width: '100%', maxWidth: '400px'}}
          >
            Tu navegador no soporta el elemento audio.
          </audio>
        </div>
      );
    };
    
    return (
      <div style={{width: '100%', height: '100%'}}>
        <AuthenticatedAudio />
      </div>
    );
  }
  
  // Si tiene openUrl, mostrar botón para abrir con la app local
  if (archivo.openUrl) {
    return (
      <div style={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem'}}>
        <a href={archivo.openUrl} target="_blank" rel="noopener noreferrer" style={{
          fontWeight:'bold',
          color:'#007bff', 
          textDecoration: 'none',
          padding: '12px 24px',
          background: '#f0f8ff',
          borderRadius: '6px',
          border: '1px solid #007bff'
        }}>
          Abrir con aplicación local ↗
        </a>
        <a href={archivo.url} target="_blank" rel="noopener noreferrer" style={{color: '#007bff', fontSize: '12px'}}>
          Abrir archivo web ↗
        </a>
      </div>
    );
  }
  // Otros tipos: solo enlace
  return (
    <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
      <a href={archivo.url} target="_blank" rel="noopener noreferrer" style={{
        color: '#007bff',
        textDecoration: 'none',
        padding: '12px 24px',
        background: '#f0f8ff',
        borderRadius: '6px',
        border: '1px solid #007bff'
      }}>
        Abrir archivo ↗
      </a>
    </div>
  );
}

const FilesPage = () => {
  const { id } = useParams();
  return <FilesComponent expId={id} />;
};

export default FilesPage;
